import { adminApiClient } from './adminApiClient';
import { createCrudService } from './createCrudService';
import type { User } from './usersService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Tenant {
  id: number;
  slug: string;
  name: string;
  domain: string | null;
  settings: Record<string, unknown> | null;
  is_active: boolean;
  user_count: number;
  created_at: string;
  updated_at: string;
}

export interface TenantModule {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  version: string | null;
  is_enabled: boolean;
  config: Record<string, unknown> | null;
  assigned_at: string;
}

export interface StoreTenantRequest {
  slug: string;
  name: string;
  domain?: string | null;
  settings?: Record<string, unknown> | null;
  is_active?: boolean;
}

export interface UpdateTenantRequest {
  slug?: string;
  name?: string;
  domain?: string | null;
  settings?: Record<string, unknown> | null;
  is_active?: boolean;
}

export interface AddUserToTenantRequest {
  user_id: number;
  role?: string | null;
}

export interface AssignModuleToTenantRequest {
  is_enabled?: boolean;
  config?: Record<string, unknown> | null;
}

export interface UpdateTenantModuleRequest {
  is_enabled?: boolean;
  config?: Record<string, unknown> | null;
}

export interface TenantRegistry {
  id: string;
  slug: string;
  name: string;
  base_url: string;
  auth_type: 'api_key' | 'oauth2_client' | 'basic' | 'bearer' | 'none' | 'login_bearer';
  default_headers: Record<string, string> | null;
  timeout_ms: number;
  retry_policy: Record<string, unknown> | null;
  is_active: boolean;
  is_main: boolean;
  endpoints_count: number;
  error_mappings_count: number;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const crud = createCrudService<
  Tenant,
  StoreTenantRequest,
  UpdateTenantRequest
>('/admin/tenants');

export const tenantsService = {
  ...crud,

  // ---- Tenant Users ----

  /** Add a user to a tenant. */
  async addUser(
    tenantId: string,
    data: AddUserToTenantRequest,
  ): Promise<void> {
    await adminApiClient.post(`/admin/tenants/${tenantId}/users`, data);
  },

  /** Remove a user from a tenant. */
  async removeUser(tenantId: string, userId: string): Promise<void> {
    await adminApiClient.delete(
      `/admin/tenants/${tenantId}/users/${userId}`,
    );
  },

  /** Update an existing tenant-user pivot's role. Passing role=null
   * resets the pivot to the schema default ('member'). */
  async updateUserRole(
    tenantId: string,
    userId: string,
    role: string | null,
  ): Promise<void> {
    await adminApiClient.put(
      `/admin/tenants/${tenantId}/users/${userId}`,
      { role },
    );
  },

  /** List users belonging to a tenant. */
  async listUsers(tenantId: string): Promise<{ data: User[] }> {
    const response = await adminApiClient.get<{ data: User[] }>(
      `/admin/tenants/${tenantId}/users`,
    );
    return response.data;
  },

  // ---- Tenant Modules ----

  /** List modules assigned to a tenant. */
  async listModules(tenantId: string): Promise<{ data: TenantModule[] }> {
    const response = await adminApiClient.get<{ data: TenantModule[] }>(
      `/admin/tenants/${tenantId}/modules`,
    );
    return response.data;
  },

  /** Assign a module to a tenant. */
  async assignModule(
    tenantId: string,
    moduleSlug: string,
    data: AssignModuleToTenantRequest,
  ): Promise<void> {
    await adminApiClient.post(
      `/admin/tenants/${tenantId}/modules/${moduleSlug}`,
      data,
    );
  },

  /** Update a tenant module configuration. */
  async updateModule(
    tenantId: string,
    moduleSlug: string,
    data: UpdateTenantModuleRequest,
  ): Promise<void> {
    await adminApiClient.put(
      `/admin/tenants/${tenantId}/modules/${moduleSlug}`,
      data,
    );
  },

  /** Remove a module from a tenant. */
  async removeModule(tenantId: string, moduleSlug: string): Promise<void> {
    await adminApiClient.delete(
      `/admin/tenants/${tenantId}/modules/${moduleSlug}`,
    );
  },

  // ---- Tenant Registries ----

  /** List ERP registries associated with a tenant. */
  async listRegistries(
    tenantId: string,
  ): Promise<{ data: TenantRegistry[] }> {
    const response = await adminApiClient.get<{ data: TenantRegistry[] }>(
      `/admin/tenants/${tenantId}/registries`,
    );
    return response.data;
  },
};
